import { Box, Flex, Grid, HStack, Icon, Link as ChakraLink, SimpleGrid, Text, keyframes } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiArrowUpRight, FiCheck } from "react-icons/fi";
import type { PublishedApp } from "../data/portfolio";
import { EASE_OUT } from "./SectionHeading";
import { SpotlightCard } from "./SpotlightCard";

const MotionBox = motion(Box);

const DESKTOP_COLUMNS = 20;

// A gold highlight sweeps across the anonymous tiles, column by column.
const sweep = keyframes`
  0%, 78%, 100% { background-color: rgba(255, 255, 255, 0.14); }
  86% { background-color: rgba(232, 164, 74, 0.95); }
`;

const tileVariants = {
  hidden: { opacity: 0, scale: 0.3 },
  show: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.45, delay: i * 0.012, ease: EASE_OUT },
  }),
};

const cardVariants = {
  hidden: { opacity: 0, y: 26 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: EASE_OUT },
  }),
};

/** Anonymous grid: one tile per app, no names. */
export function AppGrid({ count }: { count: number }) {
  return (
    <MotionBox mt={10} maxW="2xl" initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
      <Text fontFamily="mono" fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="accent.300" mb={4}>
        Uma base, mais de {count} apps em produção
      </Text>
      <Grid
        templateColumns={{ base: "repeat(12, 1fr)", md: `repeat(${DESKTOP_COLUMNS}, 1fr)` }}
        gap={{ base: "5px", md: "6px" }}
        aria-hidden
      >
        {Array.from({ length: count }, (_, i) => (
          <MotionBox key={i} variants={tileVariants} custom={i} style={{ aspectRatio: "1" }}>
            <Box
              boxSize="full"
              borderRadius="6px"
              bg="whiteAlpha.200"
              animation={`${sweep} 4.5s ease-in-out infinite`}
              sx={{
                animationDelay: `${(i % DESKTOP_COLUMNS) * 0.09 + Math.floor(i / DESKTOP_COLUMNS) * 0.05}s`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            />
          </MotionBox>
        ))}
      </Grid>
    </MotionBox>
  );
}

const fillVariants = {
  hidden: { scaleX: 0 },
  show: (i: number) => ({
    scaleX: 1,
    transition: { duration: 1.1, delay: 0.5 + i * 0.12, ease: "easeInOut" },
  }),
};

const checkVariants = {
  hidden: { opacity: 0, scale: 0.4 },
  show: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.35, delay: 1.6 + i * 0.12, ease: EASE_OUT },
  }),
};

/** Illustrative store-listing skeleton, drawn here: no real logo, icon or screenshot of the app. */
function StoreListingPhone({ letter, index }: { letter: string; index: number }) {
  return (
    <Box
      w={{ base: "78px", md: "92px" }}
      flexShrink={0}
      borderRadius="18px"
      p="5px"
      bg="#08080b"
      borderWidth="1px"
      borderColor="whiteAlpha.300"
      transition="transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)"
      _groupHover={{ transform: "translateY(-4px) rotate(-3deg)" }}
      aria-hidden
    >
      <Box borderRadius="13px" bg="#111116" px="7px" pt="12px" pb="8px">
        <HStack spacing="6px" align="center">
          <Flex
            boxSize={{ base: "22px", md: "26px" }}
            flexShrink={0}
            borderRadius="7px"
            align="center"
            justify="center"
            bg="accent.400"
            color="graphite.950"
            fontFamily="heading"
            fontWeight="700"
            fontSize={{ base: "11px", md: "13px" }}
            lineHeight="1"
          >
            {letter}
          </Flex>
          <Box flex={1}>
            <Box h="5px" w="90%" borderRadius="full" bg="whiteAlpha.500" />
            <Box h="4px" w="55%" borderRadius="full" bg="whiteAlpha.200" mt="4px" />
          </Box>
        </HStack>

        <Box position="relative" mt="10px" h="14px" borderRadius="full" bg="whiteAlpha.200" overflow="hidden">
          <MotionBox
            position="absolute"
            inset={0}
            bg="green.300"
            variants={fillVariants}
            custom={index}
            style={{ transformOrigin: "left center" }}
          />
          <MotionBox position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" variants={checkVariants} custom={index}>
            <Icon as={FiCheck} boxSize="10px" color="graphite.950" strokeWidth={4} />
          </MotionBox>
        </Box>

        <HStack spacing="4px" mt="10px">
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              flex={1}
              h={{ base: "44px", md: "54px" }}
              borderRadius="5px"
              bg="whiteAlpha.200"
              animation={`${sweep} 4.5s ease-in-out infinite`}
              sx={{
                animationDelay: `${i * 0.25 + index * 0.4}s`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            />
          ))}
        </HStack>
        <Box h="4px" w="80%" borderRadius="full" bg="whiteAlpha.200" mt="9px" />
        <Box h="4px" w="60%" borderRadius="full" bg="whiteAlpha.100" mt="4px" />
      </Box>
    </Box>
  );
}

function AppCard({ app, index }: { app: PublishedApp; index: number }) {
  return (
    <MotionBox variants={cardVariants} custom={index} h="full">
      <ChakraLink href={app.storeUrl} isExternal display="block" h="full" role="group" _hover={{ textDecoration: "none" }}>
        <SpotlightCard h="full" p={{ base: 5, md: 6 }} borderRadius="2xl" borderWidth="1px" borderColor="whiteAlpha.200" bg="whiteAlpha.50">
          <Flex gap={{ base: 4, md: 5 }} align="center">
            <StoreListingPhone letter={app.name.charAt(0)} index={index} />
            <Box flex={1} minW={0}>
              <HStack justify="space-between" spacing={2}>
                <Text fontFamily="heading" fontWeight="700" fontSize="lg" color="white">
                  {app.name}
                </Text>
                <Icon
                  as={FiArrowUpRight}
                  color="graphite.400"
                  transition="transform 0.25s ease, color 0.25s ease"
                  _groupHover={{ color: "accent.300", transform: "translate(3px, -3px)" }}
                />
              </HStack>
              <Text mt={1} fontSize="sm" color="graphite.300">
                {app.description}
              </Text>
              <HStack mt={3} spacing={2} fontFamily="mono" fontSize="11px" letterSpacing="0.06em" textTransform="uppercase" color="graphite.400">
                <Box w="6px" h="6px" borderRadius="full" bg="green.300" flexShrink={0} />
                <Text as="span">
                  Google Play{app.downloads ? ` · ${app.downloads}` : ""}
                </Text>
              </HStack>
            </Box>
          </Flex>
        </SpotlightCard>
      </ChakraLink>
    </MotionBox>
  );
}

export function PublishedApps({ apps }: { apps: PublishedApp[] }) {
  return (
    <MotionBox initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {apps.map((app, i) => (
          <AppCard key={app.storeUrl} app={app} index={i} />
        ))}
      </SimpleGrid>
    </MotionBox>
  );
}
